import { SafeError } from '@benzinga/safe-await';
import { SocketData } from './sendQueue';
import { DEFAULT_CONNECT_TIMEOUT_MS, UrlProvider } from './types';

export interface TransportCallbacks<RESP> {
  /** After async URL resolution, before constructing the WebSocket. Return false to abandon this attempt (e.g. state changed while awaiting). */
  shouldProceed: () => boolean;
  /** Handshake completed; pendingWs already promoted to ws. */
  onOpened: () => void;
  onMessage: (data: RESP) => void;
  /** Transport-level error (URL provider rejection, construction throw, ws error event, send throw is NOT routed here — send stays in socket.ts). The second arg tells the caller whether this error also aborts a connection attempt (provider/construction failures) vs. being purely informational (ws onerror). */
  onError: (error: SafeError, lastErrorTag: string, attemptAborted: boolean) => void;
  /** The LIVE socket closed (ws already cleared). Caller decides clean-close vs drop routing. */
  onLiveClosed: (event: CloseEvent) => void;
  /** A pending handshake failed (pendingWs already cleared). */
  onHandshakeFailed: (event: CloseEvent) => void;
  /** The attempt (URL resolution + handshake) blew its budget; the pending socket is already aborted. */
  onConnectTimeout: () => void;
  onWarn?: (event: string) => void;
}

export class SocketTransport<RESP> {
  private ws?: WebSocket; // live, handshake completed
  private pendingWs?: WebSocket; // handshake in flight
  private connectId = 0; // guards stale async URL resolutions
  private connectTimer?: ReturnType<typeof setTimeout>; // watchdog for the attempt currently in flight

  constructor(
    private urlProvider: UrlProvider,
    private binaryType: BinaryType,
    private callbacks: TransportCallbacks<RESP>,
    private connectTimeoutMs: number = DEFAULT_CONNECT_TIMEOUT_MS,
  ) {}

  public connect = async (): Promise<void> => {
    const attemptId = ++this.connectId;
    // One watchdog per attempt, armed before the URL provider is touched: a provider that never
    // settles hangs the attempt just as thoroughly as a handshake the server never completes.
    this.armConnectTimer();
    let url: string;
    try {
      const resolved = typeof this.urlProvider === 'function' ? await this.urlProvider() : this.urlProvider;
      url = resolved.toString();
    } catch (err) {
      if (attemptId !== this.connectId) return; // superseded — a newer attempt owns the timer now
      this.clearConnectTimer();
      const safeError = new SafeError('socket url provider failed', 'socketV2', err);
      this.callbacks.onError(safeError, 'url provider failed', true);
      return;
    }
    if (attemptId !== this.connectId) return; // close()/reconnect() happened while resolving
    if (!this.callbacks.shouldProceed()) {
      this.clearConnectTimer();
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (err) {
      this.clearConnectTimer();
      const safeError = new SafeError('socket construction failed', 'socketV2', err);
      this.callbacks.onError(safeError, 'construction failed', true);
      return;
    }
    socket.binaryType = this.binaryType;
    this.pendingWs = socket;

    socket.onopen = () => {
      if (this.pendingWs !== socket) {
        try {
          socket.close(1000);
        } catch {
          this.callbacks.onWarn?.('stale-socket-close-failed');
        }
        return;
      }
      this.pendingWs = undefined;
      this.ws = socket;
      this.clearConnectTimer(); // attempt completed — the live socket is the heartbeat's problem now
      this.callbacks.onOpened();
    };

    socket.onmessage = (event: MessageEvent) => {
      if (this.ws !== socket) return;
      this.callbacks.onMessage(event.data as RESP);
    };

    socket.onerror = (event: Event) => {
      if (this.ws !== socket && this.pendingWs !== socket) return;
      this.callbacks.onError(new SafeError('socket transport error', 'socketV2', event), 'transport error', false);
    };

    socket.onclose = (event: CloseEvent) => {
      if (this.ws === socket) {
        this.ws = undefined;
        this.callbacks.onLiveClosed(event);
        return;
      }
      if (this.pendingWs === socket) {
        // Handshake failed.
        this.pendingWs = undefined;
        this.clearConnectTimer();
        this.callbacks.onHandshakeFailed(event);
      }
    };
  };

  public sendRaw = (data: SocketData): void => {
    if (!this.ws) throw new Error('sendRaw called with no live socket');
    this.ws.send(data as string | ArrayBuffer | ArrayBufferView<ArrayBuffer> | Blob);
  };

  public isLive = (): boolean => this.ws !== undefined;

  public hasPending = (): boolean => this.pendingWs !== undefined;

  public requestCleanClose = (): boolean => {
    if (!this.ws) return false;
    try {
      this.ws.close(1000);
      return true;
    } catch {
      return false;
    }
  };

  public closeGracefully = (onLateClose: (event: CloseEvent) => void): void => {
    if (!this.ws) return;
    const closing = this.ws;
    this.ws = undefined;
    closing.onopen = null;
    closing.onmessage = null;
    closing.onerror = null;
    closing.onclose = onLateClose;
    try {
      closing.close(1000);
    } catch {
      // close-failed swallowed; caller transitions to closed regardless
      this.callbacks.onWarn?.('close-failed');
    }
  };

  public teardown = (): void => {
    if (this.ws) {
      const old = this.ws;
      this.ws = undefined;
      old.onopen = null;
      old.onmessage = null;
      old.onerror = null;
      old.onclose = null;
      try {
        old.close();
      } catch {
        this.callbacks.onWarn?.('teardown-close-failed');
      }
    }
    this.abortPending();
  };

  public abortPending = (): void => {
    this.clearConnectTimer();
    if (this.pendingWs) {
      const pending = this.pendingWs;
      this.pendingWs = undefined;
      pending.onopen = null;
      pending.onmessage = null;
      pending.onerror = null;
      pending.onclose = null;
      try {
        pending.close();
      } catch {
        this.callbacks.onWarn?.('abort-close-failed');
      }
    }
    this.connectId += 1; // invalidate in-flight URL resolutions
  };

  public armConnectTimer = (): void => {
    this.clearConnectTimer();
    if (this.connectTimeoutMs <= 0) return; // disabled
    this.connectTimer = setTimeout(this.onConnectTimedOut, this.connectTimeoutMs);
  };

  public clearConnectTimer = (): void => {
    clearTimeout(this.connectTimer);
    this.connectTimer = undefined;
  };

  private onConnectTimedOut = (): void => {
    this.connectTimer = undefined;
    // Same teardown a caller-driven abort performs: handlers nulled, socket closed, connectId
    // bumped so a late URL resolution for this attempt is discarded.
    this.abortPending();
    this.callbacks.onConnectTimeout();
  };
}
